import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Search, Filter, X, ArrowRight, ArrowLeft, Calendar, Clock,
  Grid, List, Bookmark, Eye, Share2, ChevronDown
} from 'lucide-react';

// Import simplified components
import HeaderNav from '../components/HeaderNav';
import FooterLan from '../components/FooterLan';
import Button from '../components/Button';

// --- Color Palette ---
const COLORS = {
  primary: '#ce2223',       // Lusidea Red
  primaryLight: '#f8e7e7',  
  secondary: '#fdb52a',     // Accent Yellow/Gold
  secondaryLight: '#fff8eb', 
  black: '#111827',         
  darkGray: '#374151',      
  mediumGray: '#6b7280',    
  lightGray: '#e5e7eb',     
  offWhite: '#f9fafb',      
  white: '#ffffff'
};

// --- Spacing System ---
const SPACING = {
  container: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
};

// --- SEO Component ---
const SEO = ({ title, description, type = 'website', url = '', image = '' }) => {
  useEffect(() => {
    // Set page title
    document.title = title || 'Innovation Insights | Lusidea Knowledge Center';
    
    // Update meta tags
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description || 'Explore Lusidea\'s latest innovation insights, methodologies, and success stories across industries.');
    }

    // Add or update Open Graph and Twitter meta tags
    const metaTags = {
      'og:title': title || 'Innovation Insights | Lusidea Knowledge Center',
      'og:description': description || 'Explore Lusidea\'s latest innovation insights, methodologies, and success stories across industries.',
      'og:type': type,
      'og:url': url || window.location.href,
      'og:image': image || '/images/lusidea-social-share.jpg',
      'twitter:card': 'summary_large_image',
      'twitter:title': title || 'Innovation Insights | Lusidea Knowledge Center',
      'twitter:description': description || 'Explore Lusidea\'s latest innovation insights, methodologies, and success stories across industries.',
      'twitter:image': image || '/images/lusidea-social-share.jpg',
    };

    // Create or update meta tags
    Object.entries(metaTags).forEach(([name, content]) => {
      let meta = document.querySelector(`meta[property="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });
  }, [title, description, type, url, image]);
  
  return null;
};

// --- Simplified Cover Image Component ---
const ArticleCover = ({ title, alt }) => {
  // Generate a simple, consistent color based on the title
  const generateColor = (text) => {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Create color from a limited palette
    const colors = [
      { bg: '#f3f4f6', accent: '#4b5563' },  // Gray
      { bg: '#fee2e2', accent: '#b91c1c' },  // Red
      { bg: '#e0f2fe', accent: '#0369a1' },  // Blue
      { bg: '#f0fdf4', accent: '#15803d' },  // Green
      { bg: '#fef3c7', accent: '#b45309' },  // Yellow
      { bg: '#f5f3ff', accent: '#6d28d9' },  // Purple
      { bg: '#fff7ed', accent: '#c2410c' },  // Orange
      { bg: '#fdf2f8', accent: '#be185d' },  // Pink
    ];
    
    return colors[Math.abs(hash) % colors.length];
  };
  
  const { bg, accent } = generateColor(title);
  
  return (
    <div 
      className="w-full h-full flex items-center justify-center relative overflow-hidden"
      style={{ backgroundColor: bg }}
      role="img"
      aria-label={alt || title}
    >
      {/* Simple decorative elements */}
      <div className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-20"
        style={{ backgroundColor: accent }}
        aria-hidden="true">
      </div>
      <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full opacity-10"
        style={{ backgroundColor: accent }}
        aria-hidden="true">
      </div>
      
      {/* Content container */}
      <div className="relative z-10 flex flex-col items-center max-w-[80%]">
        {/* Title display */}
        <div className="bg-white rounded-lg shadow-md px-4 py-3 text-center">
          <h4 className="text-sm font-bold line-clamp-2" style={{ color: accent }}>
            {title}
          </h4>
        </div>
      </div>
    </div>
  );
};

// --- Loading Skeleton ---
const SkeletonCard = () => (
  <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-lightGray/30 flex flex-col h-full animate-pulse">
    <div className="h-44 bg-lightGray/40"></div>
    <div className="p-5 flex flex-col flex-grow gap-2">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-6 w-16 bg-lightGray/40 rounded-full"></div>
        <div className="h-4 w-20 bg-lightGray/40 rounded-full"></div>
      </div>
      <div className="h-6 w-full bg-lightGray/40 rounded-md mb-2"></div>
      <div className="h-6 w-4/5 bg-lightGray/40 rounded-md mb-3"></div>
      <div className="h-4 w-full bg-lightGray/40 rounded-md mb-1"></div>
      <div className="h-4 w-2/3 bg-lightGray/40 rounded-md mb-4"></div>
    </div>
  </div>
);

// --- Main Blog Index Component ---
const BlogIndexPage = () => {
  // --- State Management ---
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI state
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('lusidea-blog-view-mode') || 'grid';
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  
  // Filtering and pagination state
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(9);
  const [sortBy, setSortBy] = useState('newest');
  
  // Bookmarked posts
  const [bookmarkedPosts, setBookmarkedPosts] = useState(() => {
    const saved = localStorage.getItem('lusidea-bookmarked-posts');
    return saved ? JSON.parse(saved) : [];
  });
  
  // For URL parameters
  const location = useLocation();
  const navigate = useNavigate();
  
  // Refs
  const searchInputRef = useRef(null);
  const filterRef = useRef(null);
  const sortRef = useRef(null);
  
  // --- Side Effects ---
  
  // Handle click outside to close dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setFilterOpen(false);
      }
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setSortOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('lusidea-blog-view-mode', viewMode);
  }, [viewMode]);
  
  // Save bookmarks
  useEffect(() => {
    localStorage.setItem('lusidea-bookmarked-posts', JSON.stringify(bookmarkedPosts));
  }, [bookmarkedPosts]);
  
  // Load posts
  useEffect(() => {
    setLoading(true);
    setError(null);
    
    // Simulate fetch from an API
    const fetchData = async () => {
      try {
        // In a real app, this would be a fetch call to your API
        const response = await fetch('/data/blog.json');
        
        if (!response.ok) {
          throw new Error(`Failed to fetch blog data: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Sort posts by published date, newest first
        const sortedPosts = data.sort((a, b) => 
          new Date(b['Published On']) - new Date(a['Published On'])
        );
        
        // Add view count and read time fields
        const enhancedPosts = sortedPosts.map(post => ({
          ...post,
          viewCount: Math.floor(Math.random() * 1500) + 200,
          readTime: calculateReadTime(post['Blog Post - Richt Text'])
        }));
        
        setPosts(enhancedPosts);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog data:', err);
        setError(err.message);
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Handle URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const category = params.get('category');
    const search = params.get('search');
    const page = params.get('page');
    const sort = params.get('sort');
    
    if (category) setActiveCategory(category);
    if (search) {
      setSearchQuery(search);
      if (searchInputRef.current) {
        searchInputRef.current.value = search;
      }
    }
    if (page) setCurrentPage(parseInt(page, 10) || 1);
    if (sort) setSortBy(sort);
    
  }, [location.search]);
  
  // Apply filters when posts or filter criteria change
  useEffect(() => {
    if (posts.length > 0) {
      filterPosts(activeCategory, searchQuery, sortBy);
    }
  }, [posts, activeCategory, searchQuery, sortBy]);
  
  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (activeCategory && activeCategory !== 'All') {
      params.set('category', activeCategory);
    }
    
    if (searchQuery) {
      params.set('search', searchQuery);
    }
    
    if (currentPage > 1) {
      params.set('page', currentPage.toString());
    }
    
    if (sortBy && sortBy !== 'newest') {
      params.set('sort', sortBy);
    }
    
    const newSearch = params.toString() ? `?${params.toString()}` : '';
    navigate({ search: newSearch }, { replace: true });
  }, [activeCategory, searchQuery, currentPage, sortBy, navigate]);
  
  // --- Functions ---
  
  // Filter posts based on category, search, and sort
  const filterPosts = (category, query, sort = 'newest') => {
    let filtered = [...posts];
    
    // Filter by category
    if (category && category !== 'All') {
      filtered = filtered.filter(post => post['New Category'] === category);
    }
    
    // Filter by search query
    if (query) {
      const lowerQuery = query.toLowerCase();
      filtered = filtered.filter(post => 
        post['Blog Post - Title'].toLowerCase().includes(lowerQuery) || 
        post['Blog Post - Short Description'].toLowerCase().includes(lowerQuery) ||
        post['New Category'].toLowerCase().includes(lowerQuery)
      );
    }
    
    // Sort posts
    filtered = sortPosts(filtered, sort);
    
    setFilteredPosts(filtered);
    if (currentPage > Math.ceil(filtered.length / postsPerPage)) {
      setCurrentPage(1);
    }
  };
  
  // Sort posts based on criteria
  const sortPosts = (postsToSort, criterion) => {
    switch (criterion) {
      case 'newest':
        return [...postsToSort].sort((a, b) => 
          new Date(b['Published On']) - new Date(a['Published On'])
        );
      case 'oldest':
        return [...postsToSort].sort((a, b) => 
          new Date(a['Published On']) - new Date(b['Published On'])
        );
      case 'popular':
        return [...postsToSort].sort((a, b) => 
          (b.viewCount || 0) - (a.viewCount || 0)
        );
      case 'title':
        return [...postsToSort].sort((a, b) => 
          a['Blog Post - Title'].localeCompare(b['Blog Post - Title'])
        );
      default:
        return postsToSort;
    }
  };
  
  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    const query = searchInputRef.current.value.trim();
    setSearchQuery(query);
    setCurrentPage(1);
  };
  
  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
    setCurrentPage(1);
  };
  
  // Handle category change
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setCurrentPage(1);
    setFilterOpen(false); // Close filter dropdown on mobile
  };
  
  // Handle sort change
  const handleSortChange = (sort) => {
    setSortBy(sort);
    setCurrentPage(1);
    setSortOpen(false); // Close sort dropdown
  };
  
  // Toggle bookmark for a post
  const toggleBookmark = (postLink) => {
    setBookmarkedPosts(prev => {
      if (prev.includes(postLink)) {
        return prev.filter(link => link !== postLink);
      } else {
        return [...prev, postLink];
      }
    });
  };
  
  // Share article
  const shareArticle = (post) => {
    if (navigator.share) {
      navigator.share({
        title: post['Blog Post - Title'],
        text: post['Blog Post - Short Description'],
        url: `/blog/${post['Blog Post - Link']}`
      })
      .catch(error => console.log('Error sharing', error));
    } else {
      // Fallback to clipboard copy
      navigator.clipboard.writeText(window.location.origin + `/blog/${post['Blog Post - Link']}`)
        .then(() => {
          // Show a toast notification - would need to implement a toast system
          console.log('URL copied to clipboard');
        })
        .catch(err => console.error('Failed to copy: ', err));
    }
  };
  
  // Calculate all unique categories
  const categories = ['All', ...new Set(posts.map(post => post['New Category']))];
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  
  // Pagination controls
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Smooth scroll to top of articles section
    document.getElementById('articles-section')?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };
  
  // Calculate read time based on content length
  function calculateReadTime(content) {
    if (!content) return 1;
    // Strip HTML tags and count words
    const text = content.replace(/<[^>]*>/g, '');
    const wordCount = text.split(/\s+/).length;
    // Average reading speed: 200-250 words per minute
    const minutes = Math.ceil(wordCount / 225);
    return Math.max(1, minutes); // Minimum 1 minute
  }
  
  // Format date nicely
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  // --- Generate structured JSON-LD data for SEO ---
  const generateJsonLd = () => {
    const data = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Lusidea Knowledge Center",
      "url": window.location.href,
      "description": "Explore Lusidea's latest innovation insights, methodologies, and success stories across industries."
    };
    
    return JSON.stringify(data);
  };
  
  // --- Render Content Based on Loading State ---
  
  // Loading Skeleton
  if (loading) {
    return (
      <div className="font-sans text-darkGray bg-white min-h-screen">
        <HeaderNav />
        
        {/* Hero section skeleton */}
        <section className="pt-20 sm:pt-24 lg:pt-28 pb-16 bg-gradient-to-br from-primaryLight via-white to-secondaryLight">
          <div className={SPACING.container}>
            <div className="max-w-3xl mx-auto mb-12 text-center">
              <div className="inline-block rounded-full bg-primaryLight/40 h-6 w-32 mx-auto mb-5"></div>
              <div className="h-12 bg-lightGray/40 rounded-lg mx-auto mb-6 max-w-2xl"></div>
              <div className="h-8 bg-lightGray/40 rounded-lg mx-auto max-w-xl"></div>
            </div>
            
            <div className="max-w-xl mx-auto">
              <div className="h-12 bg-lightGray/40 rounded-full"></div>
            </div>
          </div>
        </section>
        
        {/* Articles grid skeleton */}
        <section className="py-12 bg-white">
          <div className={SPACING.container}>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          </div>
        </section>
        
        <FooterLan />
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="font-sans text-darkGray bg-gradient-to-br from-primaryLight via-white to-secondaryLight min-h-screen flex items-center justify-center">
        <SEO title="Error | Lusidea Blog" />
        <div className="text-center max-w-md mx-auto p-6">
          <div className="mb-5 w-16 h-16 mx-auto rounded-full bg-primaryLight flex items-center justify-center">
            <X size={28} className="text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-black mb-3">Something Went Wrong</h1>
          <p className="text-md text-darkGray mb-5">We're having trouble loading our blog articles.</p>
          <p className="text-sm text-mediumGray mb-6">Error: {error}</p>
          <Button 
            variant="primary" 
            size="md"
            onClick={() => window.location.reload()}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // --- Main Content ---
  return (
    <div className="font-sans text-darkGray bg-white">
      <HeaderNav />
      <SEO 
        title={searchQuery 
          ? `Search results for "${searchQuery}" | Lusidea Blog` 
          : activeCategory !== 'All' 
            ? `${activeCategory} Articles | Lusidea Blog` 
            : "Innovation Insights | Lusidea Knowledge Center"
        }
        description={searchQuery 
          ? `Browse articles related to "${searchQuery}" in the Lusidea Knowledge Center.`
          : activeCategory !== 'All'
            ? `Explore our collection of articles about ${activeCategory.toLowerCase()} in the Lusidea Knowledge Center.`
            : "Explore Lusidea's latest innovation insights, methodologies, and success stories across industries."
        }
      />
      
      {/* Insert JSON-LD structured data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: generateJsonLd() }} />
      
      {/* Hero Section */}
      <section className="pt-20 sm:pt-24 lg:pt-28 pb-14 sm:pb-16 bg-gradient-to-br from-primaryLight via-white to-secondaryLight">
        <div className={SPACING.container}>
          <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-12">
            <div className="inline-block rounded-full bg-primaryLight px-4 py-1.5 mb-5">
              <h2 className="text-xs sm:text-sm font-semibold text-primary uppercase tracking-wide">
                Lusidea Knowledge Center
              </h2>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-6 leading-tight">
              Innovation <span className="text-primary">
                Insights
              </span> & Ideas
            </h1>
            <p className="text-base sm:text-lg text-darkGray max-w-2xl mx-auto leading-relaxed">
              Explore our latest thinking on innovation strategies, methodologies, and success stories across industries.
            </p>
          </div>
          
          {/* Search Bar */}
          <div className="max-w-xl mx-auto">
            <form 
              onSubmit={handleSearch}
              className="relative flex items-center"
            >
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search articles, topics, and more..."
                className="w-full px-4 sm:px-5 py-3 sm:py-4 pr-14 rounded-full border border-lightGray/60 focus:outline-none focus:ring-2 focus:ring-primary/30 shadow-md"
                defaultValue={searchQuery}
                aria-label="Search articles"
              />
              
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-14 text-mediumGray hover:text-primary p-2"
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              )}
              
              <button
                type="submit"
                className="absolute right-3 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary text-white flex items-center justify-center transition-colors hover:bg-primary/90 focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                aria-label="Search"
              >
                <Search size={16} className="sm:hidden" />
                <Search size={18} className="hidden sm:block" />
              </button>
            </form>
          </div>
        </div>
      </section>
      
      {/* Filter Bar */}
      <section className="py-3 md:py-4 bg-offWhite border-y border-lightGray/30 sticky top-0 z-30 shadow-sm">
        <div className={SPACING.container}>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
            {/* Category filters - Desktop */}
            <div className="hidden md:flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                    activeCategory === category 
                    ? 'bg-primary text-white focus:ring-primary/50' 
                    : 'bg-white text-darkGray hover:bg-primaryLight hover:text-primary focus:ring-primary/30'
                  }`}
                  aria-pressed={activeCategory === category}
                >
                  {category}
                </button>
              ))}
            </div>
            
            {/* Mobile filter controls */}
            <div className="md:hidden w-full flex items-center justify-between gap-2">
              {/* Category filter dropdown */}
              <div className="relative flex-1" ref={filterRef}>
                <button
                  className="flex-1 flex items-center justify-between px-3 py-2 bg-white rounded-lg shadow-sm border border-lightGray/60 w-full"
                  onClick={() => setFilterOpen(!filterOpen)}
                  aria-expanded={filterOpen}
                >
                  <div className="flex items-center">
                    <Filter size={14} className="mr-1.5 text-primary" />
                    <span className="font-medium text-sm truncate">{activeCategory}</span>
                  </div>
                  <ChevronDown size={14} className={`transform transition-transform ${filterOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {filterOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 p-3 bg-white rounded-lg shadow-lg border border-lightGray/60 z-40">
                    <div className="flex flex-col gap-1 max-h-56 overflow-y-auto">
                      {categories.map((category) => (
                        <button
                          key={category}
                          onClick={() => handleCategoryChange(category)}
                          className={`px-3 py-2 text-left text-sm rounded-md ${
                            activeCategory === category 
                            ? 'bg-primaryLight text-primary font-medium' 
                            : 'text-darkGray hover:bg-offWhite'
                          }`}
                          aria-pressed={activeCategory === category}
                        >
                          {category}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Sort by dropdown for mobile */}
              <div className="relative" ref={sortRef}>
                <button
                  onClick={() => setSortOpen(!sortOpen)}
                  className="px-3 py-2 bg-white rounded-lg shadow-sm border border-lightGray/60 text-sm flex items-center gap-1"
                >
                  <span className="sr-only">Sort by:</span>
                  {sortBy === 'newest' && 'Newest'}
                  {sortBy === 'oldest' && 'Oldest'}
                  {sortBy === 'popular' && 'Popular'}
                  {sortBy === 'title' && 'Title'}
                  <ChevronDown size={14} className={`transform transition-transform ${sortOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {sortOpen && (
                  <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg border border-lightGray/60 z-40 w-32">
                    <div className="py-1">
                      {[
                        { value: 'newest', label: 'Newest' },
                        { value: 'oldest', label: 'Oldest' },
                        { value: 'popular', label: 'Popular' },
                        { value: 'title', label: 'Title A-Z' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleSortChange(option.value)}
                          className={`block w-full text-left px-3 py-2 text-sm ${
                            sortBy === option.value
                              ? 'bg-primaryLight text-primary font-medium'
                              : 'text-darkGray hover:bg-offWhite'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* View mode toggle for mobile */}
              <div className="flex bg-white rounded-lg shadow-sm border border-lightGray/60">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-l-lg ${viewMode === 'grid' ? 'bg-primaryLight text-primary' : 'text-mediumGray'}`}
                  aria-label="Grid view"
                  aria-pressed={viewMode === 'grid'}
                >
                  <Grid size={14} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-r-lg ${viewMode === 'list' ? 'bg-primaryLight text-primary' : 'text-mediumGray'}`}
                  aria-label="List view"
                  aria-pressed={viewMode === 'list'}
                >
                  <List size={14} />
                </button>
              </div>
            </div>
            
            {/* Desktop controls */}
            <div className="hidden md:flex items-center gap-3">
              {/* Sort dropdown - Desktop */}
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="appearance-none px-4 py-2 pr-8 bg-white rounded-lg shadow-sm border border-lightGray/60 text-darkGray font-medium focus:outline-none focus:ring-2 focus:ring-primary/30"
                  aria-label="Sort articles"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="popular">Most Popular</option>
                  <option value="title">Title A-Z</option>
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-mediumGray" />
              </div>
              
              {/* View mode toggle */}
              <div className="flex bg-white rounded-lg shadow-sm border border-lightGray/60">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-l-lg transition-colors ${viewMode === 'grid' ? 'bg-primaryLight text-primary' : 'text-mediumGray hover:text-primary/80'}`}
                  aria-label="Grid view"
                  aria-pressed={viewMode === 'grid'}
                >
                  <Grid size={16} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-r-lg transition-colors ${viewMode === 'list' ? 'bg-primaryLight text-primary' : 'text-mediumGray hover:text-primary/80'}`}
                  aria-label="List view"
                  aria-pressed={viewMode === 'list'}
                >
                  <List size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Articles Section */}
      <section id="articles-section" className="py-10 sm:py-12 lg:py-16 bg-white min-h-screen">
        <div className={SPACING.container}>
          {/* Search result information */}
          {(searchQuery || activeCategory !== 'All') && (
            <div className="mb-8 bg-offWhite rounded-lg p-4 sm:p-5 shadow-sm border border-lightGray/30">
              <h2 className="text-lg sm:text-xl font-bold text-black mb-2">
                {searchQuery ? (
                  <span>Search results for <span className="text-primary">"{searchQuery}"</span></span>
                ) : (
                  <span>Articles in <span className="text-primary">{activeCategory}</span></span>
                )}
              </h2>
              
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-darkGray">
                  Found {filteredPosts.length} {filteredPosts.length === 1 ? 'article' : 'articles'}
                </span>
                
                {(searchQuery || activeCategory !== 'All') && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setActiveCategory('All');
                      if (searchInputRef.current) {
                        searchInputRef.current.value = '';
                      }
                    }}
                    className="text-sm font-medium text-primary hover:underline flex items-center"
                  >
                    <X size={14} className="mr-1" />
                    Clear filters
                  </button>
                )}
              </div>
            </div>
          )}
          
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12 sm:py-16">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primaryLight mb-6">
                <Search size={32} className="text-primary" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-black mb-3">No articles found</h2>
              <p className="text-mediumGray mb-8 max-w-lg mx-auto">
                We couldn't find any articles matching your search criteria. Try adjusting your filters or search terms.
              </p>
              <Button
                variant="primary"
                size="md"
                onClick={() => {
                  setSearchQuery('');
                  setActiveCategory('All');
                  if (searchInputRef.current) {
                    searchInputRef.current.value = '';
                  }
                }}
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <>
              {/* Grid View */}
              {viewMode === 'grid' && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                  {currentPosts.map((post) => (
                    <article
                      key={post['Blog Post - Link']}
                      className="bg-white rounded-lg shadow-sm overflow-hidden border border-lightGray/20 hover:shadow-md transition-shadow duration-200 flex flex-col h-full"
                    >
                      {/* Cover Image */}
                      <Link to={`/blog/${post['Blog Post - Link']}`} className="block h-48 relative overflow-hidden">
                        {post['Image'] ? (
                          <img 
                            src={post['Image']} 
                            alt={post['Blog Post - Title']} 
                            className="w-full h-full object-cover transition-transform duration-300 hover:scale-102"
                            loading="lazy"
                          />
                        ) : (
                          <ArticleCover 
                            title={post['Blog Post - Title']} 
                            alt={`Cover for article: ${post['Blog Post - Title']}`}
                          />
                        )}
                        
                        {/* Bookmark indicator */}
                        {bookmarkedPosts.includes(post['Blog Post - Link']) && (
                          <div className="absolute top-3 right-3 bg-white/90 rounded-full p-1.5 shadow-sm z-10">
                            <Bookmark size={14} className="text-secondary" fill={COLORS.secondary} />
                          </div>
                        )}
                      </Link>
                      
                      <div className="p-4 sm:p-5 flex flex-col flex-grow">
                        {/* Category pill */}
                        <div className="mb-2">
                          <Link 
                            to={`/blog?category=${post['New Category']}`}
                            className="text-xs font-medium text-primary bg-primaryLight px-2 py-1 rounded-full hover:bg-primary hover:text-white transition-colors inline-block"
                          >
                            {post['New Category']}
                          </Link>
                        </div>
                        
                        {/* Title */}
                        <Link to={`/blog/${post['Blog Post - Link']}`}>
                          <h2 className="text-base sm:text-lg font-bold text-black mb-2 hover:text-primary transition-colors line-clamp-2">
                            {post['Blog Post - Title']}
                          </h2>
                        </Link>
                        
                        {/* Date and read time */}
                        <div className="flex items-center gap-3 text-xs text-mediumGray mb-2">
                          <span className="flex items-center">
                            <Calendar size={12} className="mr-1"/>
                            {formatDate(post['Published On'])}
                          </span>
                          <span className="flex items-center">
                            <Clock size={12} className="mr-1"/>
                            {post.readTime} min read
                          </span>
                        </div>
                        
                        <p className="text-sm text-darkGray line-clamp-3 mb-4 flex-grow">
                          {post['Blog Post - Short Description']}
                        </p>
                        
                        <div className="flex items-center justify-between mt-auto pt-3 border-t border-lightGray/30">
                          <Link 
                            to={`/blog/${post['Blog Post - Link']}`}
                            className="inline-flex items-center text-sm font-medium text-primary hover:underline"
                            aria-label={`Read article: ${post['Blog Post - Title']}`}
                          >
                            Read Article <ArrowRight size={14} className="ml-1"/>
                          </Link>
                          
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => toggleBookmark(post['Blog Post - Link'])}
                              className={`p-1.5 rounded-full hover:bg-offWhite transition-colors`}
                              aria-label={bookmarkedPosts.includes(post['Blog Post - Link']) ? "Remove bookmark" : "Bookmark article"}
                            >
                              <Bookmark 
                                size={14} 
                                fill={bookmarkedPosts.includes(post['Blog Post - Link']) ? COLORS.secondary : "none"} 
                                className={bookmarkedPosts.includes(post['Blog Post - Link']) ? "text-secondary" : "text-mediumGray"}
                              />
                            </button>
                            <button
                              onClick={() => shareArticle(post)}
                              className="p-1.5 rounded-full hover:bg-offWhite transition-colors text-mediumGray"
                              aria-label="Share article"
                            >
                              <Share2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
              
              {/* List View */}
              {viewMode === 'list' && (
                <div className="space-y-5 sm:space-y-6">
                  {currentPosts.map((post) => (
                    <article
                      key={post['Blog Post - Link']}
                      className="bg-white rounded-lg shadow-sm overflow-hidden border border-lightGray/20 hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-0">
                        {/* Cover image */}
                        <Link 
                          to={`/blog/${post['Blog Post - Link']}`} 
                          className="h-48 md:h-full relative overflow-hidden"
                        >
                          {post['Image'] ? (
                            <img 
                              src={post['Image']} 
                              alt={post['Blog Post - Title']} 
                              className="w-full h-full object-cover transition-transform duration-300 hover:scale-102"
                              loading="lazy"
                            />
                          ) : (
                            <ArticleCover 
                              title={post['Blog Post - Title']}
                              alt={`Cover for article: ${post['Blog Post - Title']}`}
                            />
                          )}
                          
                          {/* Bookmark indicator */}
                          {bookmarkedPosts.includes(post['Blog Post - Link']) && (
                            <div className="absolute top-3 right-3 bg-white/90 rounded-full p-1.5 shadow-sm z-10">
                              <Bookmark size={14} className="text-secondary" fill={COLORS.secondary} />
                            </div>
                          )}
                        </Link>
                        
                        <div className="md:col-span-2 lg:col-span-3 p-4 sm:p-5 md:p-6">
                          {/* Category link */}
                          <div className="mb-2">
                            <Link 
                              to={`/blog?category=${post['New Category']}`}
                              className="text-xs font-medium text-primary bg-primaryLight px-2 py-1 rounded-full hover:bg-primary hover:text-white transition-colors inline-block"
                            >
                              {post['New Category']}
                            </Link>
                          </div>
                          
                          {/* Title */}
                          <Link to={`/blog/${post['Blog Post - Link']}`}>
                            <h2 className="text-lg sm:text-xl font-bold text-black mb-2 hover:text-primary transition-colors">
                              {post['Blog Post - Title']}
                            </h2>
                          </Link>
                          
                          {/* Meta info */}
                          <div className="flex flex-wrap items-center gap-3 text-xs text-mediumGray mb-3">
                            <span className="flex items-center">
                              <Calendar size={12} className="mr-1"/>
                              {formatDate(post['Published On'])}
                            </span>
                            <span className="flex items-center">
                              <Clock size={12} className="mr-1"/>
                              {post.readTime} min read
                            </span>
                            <span className="flex items-center">
                              <Eye size={12} className="mr-1"/>
                              {post.viewCount.toLocaleString()} views
                            </span>
                          </div>
                          
                          <p className="text-sm text-darkGray mb-4 line-clamp-2 md:line-clamp-3">
                            {post['Blog Post - Short Description']}
                          </p>
                          
                          <div className="flex items-center justify-between">
                            <Link
                              to={`/blog/${post['Blog Post - Link']}`}
                              className="inline-flex items-center font-medium text-primary hover:underline"
                              aria-label={`Read article: ${post['Blog Post - Title']}`}
                            >
                              Read Full Article <ArrowRight size={14} className="ml-1.5"/>
                            </Link>
                            
                            {/* Action buttons */}
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleBookmark(post['Blog Post - Link'])}
                                className="p-1.5 rounded-full hover:bg-offWhite transition-colors"
                                aria-label={bookmarkedPosts.includes(post['Blog Post - Link']) ? "Remove bookmark" : "Bookmark article"}
                              >
                                <Bookmark 
                                  size={16} 
                                  fill={bookmarkedPosts.includes(post['Blog Post - Link']) ? COLORS.secondary : "none"} 
                                  className={bookmarkedPosts.includes(post['Blog Post - Link']) ? "text-secondary" : "text-mediumGray"}
                                />
                              </button>
                              <button
                                onClick={() => shareArticle(post)}
                                className="p-1.5 rounded-full hover:bg-offWhite transition-colors text-mediumGray"
                                aria-label="Share article"
                              >
                                <Share2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
              
              {/* Simplified Pagination */}
              {totalPages > 1 && (
                <nav aria-label="Pagination" className="mt-10 sm:mt-12">
                  <ul className="flex flex-wrap justify-center items-center gap-2">
                    <li>
                      <button
                        onClick={() => paginate(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className={`p-2 rounded-md border ${currentPage === 1 ? 'border-lightGray text-mediumGray cursor-not-allowed opacity-50' : 'border-primary text-primary hover:bg-primaryLight'}`}
                        aria-label="Previous page"
                      >
                        <ArrowLeft size={16} />
                      </button>
                    </li>
                    
                    {/* Dynamic page numbers with ellipsis for many pages */}
                    {totalPages <= 7 ? (
                      // Show all pages if 7 or fewer
                      [...Array(totalPages)].map((_, i) => (
                        <li key={i + 1}>
                          <button
                            onClick={() => paginate(i + 1)}
                            className={`w-9 h-9 rounded-md ${
                              currentPage === i + 1
                                ? 'bg-primary text-white'
                                : 'bg-white text-darkGray hover:bg-primaryLight hover:text-primary'
                            }`}
                            aria-label={`Page ${i + 1}`}
                            aria-current={currentPage === i + 1 ? "page" : undefined}
                          >
                            {i + 1}
                          </button>
                        </li>
                      ))
                    ) : (
                      // Show limited pages with ellipsis for many pages
                      <>
                        {/* First page */}
                        <li>
                          <button
                            onClick={() => paginate(1)}
                            className={`w-9 h-9 rounded-md ${
                              currentPage === 1
                                ? 'bg-primary text-white'
                                : 'bg-white text-darkGray hover:bg-primaryLight hover:text-primary'
                            }`}
                            aria-label="Page 1"
                            aria-current={currentPage === 1 ? "page" : undefined}
                          >
                            1
                          </button>
                        </li>
                        
                        {/* Ellipsis or page 2 */}
                        {currentPage > 3 && (
                          <li className="text-mediumGray">…</li>
                        )}
                        
                        {/* Pages around current page */}
                        {[...Array(totalPages)].map((_, i) => {
                          const pageNum = i + 1;
                          // Only show nearby pages
                          if (
                            (pageNum !== 1 && pageNum !== totalPages) && // Skip first and last (handled separately)
                            (Math.abs(pageNum - currentPage) < 2 || // Pages near current
                            (currentPage <= 3 && pageNum <= 4) || // Show extra pages at start
                            (currentPage >= totalPages - 2 && pageNum >= totalPages - 3)) // Show extra pages at end
                          ) {
                            return (
                              <li key={pageNum}>
                                <button
                                  onClick={() => paginate(pageNum)}
                                  className={`w-9 h-9 rounded-md ${
                                    currentPage === pageNum
                                      ? 'bg-primary text-white'
                                      : 'bg-white text-darkGray hover:bg-primaryLight hover:text-primary'
                                  }`}
                                  aria-label={`Page ${pageNum}`}
                                  aria-current={currentPage === pageNum ? "page" : undefined}
                                >
                                  {pageNum}
                                </button>
                              </li>
                            );
                          }
                          return null;
                        })}
                        
                        {/* Ellipsis or second-last page */}
                        {currentPage < totalPages - 2 && (
                          <li className="text-mediumGray">…</li>
                        )}
                        
                        {/* Last page */}
                        <li>
                          <button
                            onClick={() => paginate(totalPages)}
                            className={`w-9 h-9 rounded-md ${
                              currentPage === totalPages
                                ? 'bg-primary text-white'
                                : 'bg-white text-darkGray hover:bg-primaryLight hover:text-primary'
                            }`}
                            aria-label={`Page ${totalPages}`}
                            aria-current={currentPage === totalPages ? "page" : undefined}
                          >
                            {totalPages}
                          </button>
                        </li>
                      </>
                    )}
                    
                    <li>
                      <button
                        onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className={`p-2 rounded-md border ${currentPage === totalPages ? 'border-lightGray text-mediumGray cursor-not-allowed opacity-50' : 'border-primary text-primary hover:bg-primaryLight'}`}
                        aria-label="Next page"
                      >
                        <ArrowRight size={16} />
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </>
          )}
        </div>
      </section>
      
      <FooterLan />
    </div>
  );
};

export default BlogIndexPage;